import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ComplianceType,
  complianceService,
  ComplianceChecklist,
  AssetCompliance,
  MaintenanceCompliance,
} from '../../services/complianceService';
import { maintenanceService } from '../../services/maintenanceService';   

interface ComplianceState {
  checklists: ComplianceChecklist[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  complianceTypes: ComplianceType[];
  isComplianceTypesLoading: boolean;
  complianceTypesError: string | null;

  // Asset Compliance
  assetCompliances: AssetCompliance[];
  assetCompliancesLoading: boolean;
  assetCompliancesError: string | null;
  assetCompliancesTotalCount: number;
  assetCompliancesCurrentPage: number;
  assetCompliancesTotalPages: number;
  assetCompliancesPageSize: number;

  // --- Maintenance Compliance ---
  maintenanceCompliances: MaintenanceCompliance[];
  maintenanceCompliancesLoading: boolean;
  maintenanceCompliancesError: string | null;
  maintenanceCompliancesTotalCount: number;
  maintenanceCompliancesCurrentPage: number;
  maintenanceCompliancesTotalPages: number;
  maintenanceCompliancesPageSize: number;
  maintenanceLogMap: Record<string, any>; // for maintenance log lookup
}

const initialState: ComplianceState = {
  checklists: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
  error: null,
  complianceTypes: [],
  isComplianceTypesLoading: false,
  complianceTypesError: null,

  assetCompliances: [],
  assetCompliancesLoading: false,
  assetCompliancesError: null,
  assetCompliancesTotalCount: 0,
  assetCompliancesCurrentPage: 1,
  assetCompliancesTotalPages: 1,
  assetCompliancesPageSize: 10,

  // --- Maintenance Compliance ---
  maintenanceCompliances: [],
  maintenanceCompliancesLoading: false,
  maintenanceCompliancesError: null,
  maintenanceCompliancesTotalCount: 0,
  maintenanceCompliancesCurrentPage: 1,
  maintenanceCompliancesTotalPages: 1,
  maintenanceCompliancesPageSize: 10,
  maintenanceLogMap: {},
};

// --- Asset Compliance Thunks ---
export const fetchAssetCompliances = createAsyncThunk(
  'compliance/fetchAssetCompliances',
  async ({ page, limit }: { page: number; limit: number }, { rejectWithValue }) => {
    try {
      const response = await complianceService.getAssetCompliances(page, limit);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset compliances');
    }
  }
);

export const deleteAssetCompliance = createAsyncThunk(
  'compliance/deleteAssetCompliance',
  async (id: string, { rejectWithValue }) => {
    try {
      await complianceService.deleteAssetCompliance(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset compliance');
    }
  }
);

// --- Maintenance Compliance Thunks ---
export const fetchMaintenanceCompliances = createAsyncThunk(
  'compliance/fetchMaintenanceCompliances',
  async ({ page, limit }: { page: number; limit: number }, { rejectWithValue }) => {
    try {
      const response = await complianceService.getMaintenanceCompliances(page, limit);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance compliances');
    }
  }
);

export const fetchMaintenanceLogById = createAsyncThunk(
  'compliance/fetchMaintenanceLogById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceLog(id);
      return { id, log: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance log');
    }
  }
);

// --- Existing Compliance Thunks ---
export const fetchComplianceChecklists = createAsyncThunk(
  'compliance/fetchComplianceChecklists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await complianceService.getComplianceChecklists();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch compliance checklists');
    }
  }
);

export const fetchComplianceTypes = createAsyncThunk(
  'compliance/fetchComplianceTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await complianceService.getComplianceTypes();
      return response.data.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch compliance types');
    }
  }
);

export const createComplianceType = createAsyncThunk(
  'compliance/createComplianceType',
  async (data: { name: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await complianceService.createComplianceType(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create compliance type');
    }
  }
);

export const updateComplianceType = createAsyncThunk(
  'compliance/updateComplianceType',
  async ({ id, data }: { id: string; data: { name: string; description?: string } }, { rejectWithValue }) => {
    try {
      const response = await complianceService.updateComplianceType(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update compliance type');
    }
  }
);

export const deleteComplianceType = createAsyncThunk(
  'compliance/deleteComplianceType',
  async (id: string, { rejectWithValue }) => {
    try {
      await complianceService.deleteComplianceType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete compliance type');
    }
  }
);

export const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- Asset Compliance ---
      .addCase(fetchAssetCompliances.pending, (state) => {
        state.assetCompliancesLoading = true;
        state.assetCompliancesError = null;
      })
      .addCase(fetchAssetCompliances.fulfilled, (state, action) => {
        state.assetCompliancesLoading = false;
        state.assetCompliances = action.payload.result;
        state.assetCompliancesTotalCount = action.payload.totalCount;
        state.assetCompliancesCurrentPage = action.payload.currentPage;
        state.assetCompliancesTotalPages = action.payload.totalPages;
      })
      .addCase(fetchAssetCompliances.rejected, (state, action) => {
        state.assetCompliancesLoading = false;
        state.assetCompliancesError = action.payload as string;
      })
      .addCase(deleteAssetCompliance.fulfilled, (state, action) => {
        state.assetCompliances = state.assetCompliances.filter((c) => c._id !== action.payload);
      })

      // --- Maintenance Compliance ---
      .addCase(fetchMaintenanceCompliances.pending, (state) => {
        state.maintenanceCompliancesLoading = true;
        state.maintenanceCompliancesError = null;
      })
      .addCase(fetchMaintenanceCompliances.fulfilled, (state, action) => {
        state.maintenanceCompliancesLoading = false;
        state.maintenanceCompliances = action.payload.result;
        state.maintenanceCompliancesTotalCount = action.payload.totalCount;
        state.maintenanceCompliancesCurrentPage = action.payload.currentPage;
        state.maintenanceCompliancesTotalPages = action.payload.totalPages;
      })
      .addCase(fetchMaintenanceCompliances.rejected, (state, action) => {
        state.maintenanceCompliancesLoading = false;
        state.maintenanceCompliancesError = action.payload as string;
      })
      .addCase(fetchMaintenanceLogById.fulfilled, (state, action) => {
        state.maintenanceLogMap[action.payload.id] = action.payload.log;
      })

      // --- Existing Compliance ---
      .addCase(fetchComplianceChecklists.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplianceChecklists.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checklists = action.payload.result;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchComplianceChecklists.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Compliance Types
      .addCase(fetchComplianceTypes.pending, (state) => {
        state.isComplianceTypesLoading = true;
        state.complianceTypesError = null;
      })
      .addCase(fetchComplianceTypes.fulfilled, (state, action) => {
        state.isComplianceTypesLoading = false;
        state.complianceTypes = action.payload;
      })
      .addCase(fetchComplianceTypes.rejected, (state, action) => {
        state.isComplianceTypesLoading = false;
        state.complianceTypesError = action.payload as string;
      })
      .addCase(createComplianceType.fulfilled, (state, action) => {
        state.complianceTypes.push(action.payload);
      })
      .addCase(updateComplianceType.fulfilled, (state, action) => {
        const idx = state.complianceTypes.findIndex(t => t._id === action.payload._id);
        if (idx !== -1) state.complianceTypes[idx] = action.payload;
      })
      .addCase(deleteComplianceType.fulfilled, (state, action) => {
        state.complianceTypes = state.complianceTypes.filter(t => t._id !== action.payload);
      });
  },
});

export default complianceSlice.reducer;